/**
 * Estoque em grade — o núcleo do produto.
 *
 * Três abas do protótipo entram aqui: por grade, movimentações e ajuste.
 * Inventário e transferência ficaram fora de propósito (ver
 * docs/plano/estoque-em-grade.md): contagem cega precisa de foto de saldo no
 * fechamento e transferência precisa de estoque de passagem — são fatias com
 * regra própria, não abas a mais.
 *
 * O que esta tela nunca faz é calcular saldo. Ela exibe o que
 * `saldo_sku` diz, e escreve pedindo movimento à API. INV-04 mora no banco.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Segmentado } from '../../componentes/Segmentado'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { DialogoPIN } from '../../componentes/DialogoPIN'
import { ErroApi } from '../../plataforma/api'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { useSessao } from '../../plataforma/contexto-sessao'
import {
  carregarGrade,
  carregarMovimentos,
  lancarAjuste,
  riscoDaCelula,
  FILTROS_MOVIMENTO,
  MOTIVOS,
  type Celula,
  type Grade,
  type Motivo,
  type Movimentos,
  type ProdutoGrade,
  type ResultadoAjuste,
  type Sentido,
  type Tipo,
} from '../../plataforma/estoque'
import { LayoutApp } from '../app/LayoutApp'

type Aba = 'grade' | 'movimentos' | 'ajuste'

const ABAS: { chave: Aba; rotulo: string }[] = [
  { chave: 'grade', rotulo: 'Por grade' },
  { chave: 'movimentos', rotulo: 'Movimentações' },
  { chave: 'ajuste', rotulo: 'Ajuste' },
]

export function TelaEstoque() {
  const { pode, exigePIN, podeVer } = useSessao()

  const [aba, setAba] = useState<Aba>('grade')
  const [filial, setFilial] = useState('')
  const [grade, setGrade] = useState<Grade | null>(null)
  const [erro, setErro] = useState('')
  // Muda a cada lançamento, para as abas de leitura recarregarem.
  const [versao, setVersao] = useState(0)

  const podeAjustar = pode('stock.adjust') || exigePIN('stock.adjust')

  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')

  // Debounce da busca: cada tecla disparando uma consulta que faz JOIN em
  // três tabelas é como se derruba o próprio banco em dia de movimento.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()

    carregarGrade(filial, buscaAtiva, controle.signal)
      .then((dados) => {
        setGrade(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o estoque.')
      })

    return () => controle.abort()
  }, [filial, buscaAtiva, versao])

  const subtitulo = grade
    ? `${grade.filial.nome} · ${grade.resumo.produtos} ${grade.resumo.produtos === 1 ? 'produto' : 'produtos'} · ${grade.resumo.pecas} ${grade.resumo.pecas === 1 ? 'peça' : 'peças'}`
    : 'Carregando…'

  return (
    <LayoutApp titulo="Estoque" subtitulo={subtitulo}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4 border-b border-borda">
          <div role="tablist" aria-label="Abas do estoque" className="flex gap-5">
            {ABAS.filter((item) => item.chave !== 'ajuste' || podeAjustar).map((item) => {
              const ativa = item.chave === aba
              return (
                <button
                  key={item.chave}
                  type="button"
                  role="tab"
                  aria-selected={ativa}
                  onClick={() => setAba(item.chave)}
                  className={`-mb-px border-b-2 pb-3 text-[13.5px] font-semibold ${
                    ativa ? 'border-marca text-marca' : 'border-transparent text-texto-suave'
                  }`}
                >
                  {item.rotulo}
                </button>
              )
            })}
          </div>

          <div className="flex-1" />

          {grade && grade.filiais.length > 1 && (
            <div className="mb-2.5">
              <SeletorDeFilial
                filiais={grade.filiais}
                atual={grade.filial.codigo}
                aoTrocar={setFilial}
              />
            </div>
          )}
        </div>

        {erro && (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erro}
          </div>
        )}

        {aba === 'grade' && (
          <AbaGrade grade={grade} busca={busca} aoBuscar={setBusca} />
        )}

        {aba === 'movimentos' && <AbaMovimentos filial={filial} versao={versao} />}

        {aba === 'ajuste' && podeAjustar && (
          <AbaAjuste
            filial={filial}
            aoLancar={() => setVersao((n) => n + 1)}
            podeVerCusto={podeVer('cost.read')}
          />
        )}
      </div>
    </LayoutApp>
  )
}

/* --------------------------------------------------------------- por grade */

function AbaGrade({
  grade,
  busca,
  aoBuscar,
}: {
  grade: Grade | null
  busca: string
  aoBuscar: (texto: string) => void
}) {
  if (!grade) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[104px] animate-pulse rounded-sistema bg-superficie" />
          ))}
        </div>
        <div className="h-[260px] animate-pulse rounded-sistema bg-superficie" />
      </div>
    )
  }

  const { resumo } = grade

  const kpis: { rotulo: string; valor: string; dica: string; icone: NomeIcone; cor?: string }[] = [
    {
      rotulo: 'Peças em estoque',
      valor: String(resumo.pecas),
      dica: `na unidade ${grade.filial.nome}`,
      icone: 'inventory_2',
    },
    {
      rotulo: 'Valor a custo',
      valor: formatarMoeda(resumo.valor_custo_centavos),
      dica: `a preço de venda ${formatarMoeda(resumo.valor_venda_centavos)}`,
      icone: 'payments',
    },
    {
      rotulo: 'SKUs zerados',
      valor: String(resumo.skus_zerados),
      dica: `em ${resumo.produtos} ${resumo.produtos === 1 ? 'produto' : 'produtos'}`,
      icone: 'error',
      cor: 'text-erro',
    },
    {
      rotulo: 'Abaixo do mínimo',
      valor: String(resumo.abaixo_do_minimo),
      dica: 'repor antes do fim de semana',
      icone: 'warning',
      cor: 'text-atencao-texto',
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.rotulo}
            className="rounded-sistema border border-borda bg-superficie px-4 py-3.5 shadow-[var(--shadow-cartao)]"
          >
            <div className="mb-2.5 flex items-center gap-2 text-[12.5px] text-texto-suave">
              <Icone nome={kpi.icone} tamanho={18} className="text-texto-fraco" />
              {kpi.rotulo}
            </div>
            <div className={`tabular text-[21px] font-bold ${kpi.cor ?? 'text-tinta'}`}>
              {kpi.valor}
            </div>
            <div className="mt-1 text-xs text-texto-suave">{kpi.dica}</div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-3">
          {/* `basis-[220px]` + `min-w-0`: regra 3 do "layout que já custou
              bug" da spec 01 — input dentro de composto precisa dos dois, ou
              estica o flex e empurra o irmão para fora. */}
          <label className="composto h-[34px] min-w-0 flex-1 basis-[220px] cursor-text">
            <Icone nome="search" tamanho={18} className="text-texto-fraco" />
            <input
              value={busca}
              onChange={(evento) => aoBuscar(evento.target.value)}
              placeholder="Buscar produto, código ou SKU"
            />
            <span className="sr-only">Buscar no estoque</span>
          </label>
        </div>

        {grade.produtos.length === 0 ? (
          <div className="flex flex-col items-center gap-2.5 px-4 py-12 text-center">
            <Icone nome="inventory_2" tamanho={26} className="text-inativo" />
            <p className="max-w-[340px] text-[13px] leading-[1.55] text-texto-suave">
              {busca
                ? `Nenhum produto encontrado para "${busca}".`
                : 'Nenhum produto no catálogo. Cadastre um produto e a grade de cor × tamanho aparece aqui.'}
            </p>
          </div>
        ) : (
          grade.produtos.map((produto, i) => (
            <BlocoDoProduto
              key={produto.codigo}
              produto={produto}
              ultimo={i === grade.produtos.length - 1}
            />
          ))
        )}
      </div>
    </div>
  )
}

function BlocoDoProduto({ produto, ultimo }: { produto: ProdutoGrade; ultimo: boolean }) {
  return (
    <div className={ultimo ? '' : 'border-b border-borda'}>
      <div className="flex flex-wrap items-center gap-3 bg-superficie-suave px-4 py-2.5">
        <div className="min-w-0 flex-1 basis-[200px]">
          {/* O estoque só EXIBE saldo (INV-04 mora no banco) — quem edita
              nome, categoria, preço ou grade é a ficha do produto. Sem este
              link, achar "onde mexo nisso" a partir do estoque exigia sair
              da tela e buscar de novo em Produtos. */}
          <Link
            to={`/app/produtos/${produto.codigo}/editar`}
            className="text-sm font-semibold text-tinta hover:text-marca hover:underline"
          >
            {produto.nome}
          </Link>
          <div className="tabular mt-0.5 text-xs text-texto-suave">
            {produto.codigo} · {produto.categoria} · mín {produto.minimo} por tamanho
          </div>
        </div>
        <div className="tabular text-[13px] whitespace-nowrap text-texto-suave">
          {produto.pecas} un · {formatarMoeda(produto.valor_custo_centavos)}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <caption className="sr-only">
            Saldo de {produto.nome} por cor e tamanho
          </caption>
          <thead>
            <tr>
              <th scope="col" className="px-5 py-2.5 text-left text-xs font-semibold text-texto-suave">
                Cor
              </th>
              {produto.tamanhos.map((tamanho) => (
                <th
                  key={tamanho}
                  scope="col"
                  className="min-w-[54px] px-2 py-2.5 text-center text-xs font-semibold text-texto-suave"
                >
                  {tamanho}
                </th>
              ))}
              <th scope="col" className="px-5 py-2.5 text-right text-xs font-semibold text-texto-suave">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {produto.cores.map((cor) => (
              <tr key={cor.cor}>
                <th scope="row" className="border-t border-divisoria px-5 py-2.5 text-left font-normal">
                  <span className="flex items-center gap-2.5">
                    {cor.hex && (
                      <span
                        aria-hidden
                        className="h-3.5 w-3.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
                        style={{ background: cor.hex }}
                      />
                    )}
                    <span className="text-[13px] font-medium text-tinta">{cor.cor}</span>
                  </span>
                </th>
                {cor.celulas.map((celula, i) => (
                  <td key={i} className="border-t border-divisoria px-2 py-2.5 text-center">
                    <BadgeDeSaldo celula={celula} />
                  </td>
                ))}
                <td className="tabular border-t border-divisoria px-5 py-2.5 text-right text-[13px] font-bold text-tinta">
                  {cor.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Badge de saldo de uma posição.
 *
 * Quatro estados, e o quarto é o que quase todo sistema erra: "esta cor não
 * vem neste tamanho" desenhado como travessão, não como zero. Zero vermelho
 * numa posição que não existe manda comprar o que o fornecedor não fabrica.
 */
function BadgeDeSaldo({ celula }: { celula: Celula }) {
  const risco = riscoDaCelula(celula)

  if (risco === 'ausente') {
    return (
      <span className="text-[12.5px] text-inativo" title="Esta cor não tem este tamanho">
        —
      </span>
    )
  }

  const cores = {
    zerado: 'bg-erro-suave text-erro-texto',
    baixo: 'bg-atencao-suave text-atencao-texto',
    ok: 'bg-ok-suave text-ok-texto',
  }[risco]

  const titulo =
    risco === 'zerado'
      ? 'Esgotado'
      : risco === 'baixo'
        ? `No mínimo ou abaixo (mín ${celula.estoque_minimo})`
        : `Saldo saudável (mín ${celula.estoque_minimo})`

  return (
    <span
      title={titulo}
      className={`tabular inline-flex min-w-[28px] justify-center rounded-full px-2 py-[3px] text-[12.5px] font-semibold ${cores}`}
    >
      {celula.saldo}
    </span>
  )
}

function SeletorDeFilial({
  filiais,
  atual,
  aoTrocar,
}: {
  filiais: { codigo: string; nome: string }[]
  atual: string
  aoTrocar: (codigo: string) => void
}) {
  return (
    <Segmentado
      opcoes={filiais.map((filial) => ({ chave: filial.codigo, rotulo: filial.nome }))}
      valor={atual}
      aoTrocar={aoTrocar}
      rotuloDoGrupo="Unidade"
      compacto
    />
  )
}

/* ------------------------------------------------------------ movimentações */

function AbaMovimentos({ filial, versao }: { filial: string; versao: number }) {
  const [tipo, setTipo] = useState<Tipo | ''>('')
  const [dados, setDados] = useState<Movimentos | null>(null)

  useEffect(() => {
    const controle = new AbortController()
    carregarMovimentos(filial, tipo, controle.signal)
      .then(setDados)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setDados({ linhas: [], total: 0 })
      })
    return () => controle.abort()
  }, [filial, tipo, versao])

  return (
    <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-3">
        <Segmentado
          opcoes={FILTROS_MOVIMENTO.map((filtro) => ({
            chave: filtro.chave,
            rotulo: filtro.rotulo,
          }))}
          valor={tipo}
          aoTrocar={setTipo}
          rotuloDoGrupo="Tipo de movimento"
          compacto
        />
      </div>

      {!dados ? (
        <div className="h-[200px] animate-pulse bg-superficie-suave" />
      ) : dados.linhas.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 px-4 py-12 text-center">
          <Icone nome="inventory_2" tamanho={26} className="text-inativo" />
          <p className="max-w-[340px] text-[13px] leading-[1.55] text-texto-suave">
            {tipo
              ? 'Nenhuma movimentação deste tipo nesta unidade.'
              : 'Nenhuma movimentação registrada. Todo lançamento de estoque aparece aqui, com responsável e motivo.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[840px] border-collapse">
            <thead>
              <tr className="bg-fundo">
                {['Quando', 'Tipo', 'SKU', 'Qtd', 'Responsável', 'Origem'].map((titulo) => (
                  <th
                    key={titulo}
                    scope="col"
                    className={`border-b border-borda px-3 py-2.5 text-[12.5px] font-semibold text-texto-suave ${
                      titulo === 'Qtd' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {titulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dados.linhas.map((linha, i) => (
                <tr key={`${linha.quando}-${i}`}>
                  <td className="tabular border-b border-divisoria px-3 py-2.5 text-[13px] text-tinta">
                    {formatarQuando(linha.quando)}
                  </td>
                  <td className="border-b border-divisoria px-3 py-2.5">
                    <SeloDeTipo tipo={linha.tipo} rotulo={linha.tipo_rotulo} />
                  </td>
                  <td className="border-b border-divisoria px-3 py-2.5">
                    <div className="text-[12.5px] font-semibold text-tinta">
                      {linha.produto}
                    </div>
                    <div className="tabular mt-0.5 text-[11.5px] text-texto-suave">
                      {linha.cor} · {linha.tamanho} · {linha.codigo_barras}
                    </div>
                  </td>
                  <td
                    className={`tabular border-b border-divisoria px-3 py-2.5 text-right text-sm font-bold ${
                      linha.quantidade > 0 ? 'text-ok' : 'text-erro'
                    }`}
                  >
                    {linha.quantidade > 0 ? '+' : ''}
                    {linha.quantidade}
                  </td>
                  <td className="border-b border-divisoria px-3 py-2.5 text-[12.5px] text-texto">
                    {linha.responsavel}
                    {linha.autorizador && (
                      <div className="mt-0.5 text-[11.5px] text-texto-suave">
                        autorizado por {linha.autorizador}
                      </div>
                    )}
                  </td>
                  <td className="border-b border-divisoria px-3 py-2.5 text-[12.5px] text-texto-suave">
                    {linha.motivo}
                    {linha.observacao && (
                      <div className="mt-0.5 text-[11.5px] text-texto-fraco">
                        {linha.observacao}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SeloDeTipo({ tipo, rotulo }: { tipo: Tipo; rotulo: string }) {
  const cores: Record<string, string> = {
    venda: 'bg-marca-suave text-marca-forte',
    compra: 'bg-ok-suave text-ok-texto',
    devolucao: 'bg-atencao-suave text-atencao-texto',
    ajuste: 'bg-divisoria text-texto',
    inventario: 'bg-marca-fundo-2 text-marca-forte',
    transferencia_saida: 'bg-divisoria text-texto',
    transferencia_entrada: 'bg-divisoria text-texto',
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
        cores[tipo] ?? 'bg-divisoria text-texto'
      }`}
    >
      {rotulo}
    </span>
  )
}

/**
 * `quando` vem em ISO com o fuso do servidor. Formatar no fuso do NAVEGADOR é
 * o certo aqui: quem lê o extrato está na loja, e o relógio dela é o do
 * sistema operacional dela.
 */
function formatarQuando(iso: string): string {
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return iso
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(data)
}

/* ------------------------------------------------------------------ ajuste */

function AbaAjuste({
  filial,
  aoLancar,
  podeVerCusto,
}: {
  filial: string
  aoLancar: () => void
  podeVerCusto: boolean
}) {
  const [sku, setSku] = useState('')
  const [quantidade, setQuantidade] = useState('')
  const [motivo, setMotivo] = useState<Motivo | ''>('')
  const [sentido, setSentido] = useState<Sentido>('saida')
  const [observacao, setObservacao] = useState('')

  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState('')
  const [resultado, setResultado] = useState<ResultadoAjuste | null>(null)

  // Estado do PIN: a chave da permissão que o servidor pediu no 409.
  const [pinPara, setPinPara] = useState('')
  const [erroPIN, setErroPIN] = useState('')

  const escolhido = MOTIVOS.find((item) => item.chave === motivo)
  const quantidadeNum = Number(quantidade) || 0
  const pronto = sku.trim() !== '' && quantidadeNum > 0 && motivo !== ''

  const sinal = !escolhido ? 0 : escolhido.sentido === 'ambos' ? (sentido === 'entrada' ? 1 : -1) : escolhido.sentido === 'entrada' ? 1 : -1

  const limpar = () => {
    setSku('')
    setQuantidade('')
    setMotivo('')
    setObservacao('')
    setSentido('saida')
  }

  const enviar = useCallback(
    async (pin?: string) => {
      // `pronto` já exige motivo escolhido, e o TypeScript estreita o tipo a
      // partir dele — repetir a checagem aqui seria código morto.
      if (!pronto) return

      setEnviando(true)
      setErro('')
      try {
        const saida = await lancarAjuste(
          {
            sku: sku.trim(),
            filial: filial || undefined,
            quantidade: quantidadeNum,
            motivo,
            // O sentido só viaja quando o motivo não o determina. Mandar nos
            // outros casos é pedir para a API recusar por contradição.
            sentido: escolhido?.sentido === 'ambos' ? sentido : undefined,
            observacao: observacao.trim() || undefined,
          },
          pin,
        )

        setResultado(saida)
        setPinPara('')
        setErroPIN('')
        limpar()
        aoLancar()
      } catch (causa: unknown) {
        if (!(causa instanceof ErroApi)) {
          setErro('Não foi possível lançar o ajuste.')
          return
        }

        // 409: a ação é permitida, falta a autorização de um terceiro.
        if (causa.pinNecessario) {
          setPinPara(causa.pinNecessario)
          setErroPIN('')
          return
        }
        if (causa.codigo === 'pin_invalido') {
          setErroPIN(causa.message)
          return
        }

        setPinPara('')
        setErro(causa.message)
      } finally {
        setEnviando(false)
      }
    },
    [pronto, motivo, sku, filial, quantidadeNum, escolhido, sentido, observacao, aoLancar],
  )

  return (
    <>
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
          <h2 className="font-titulo text-base font-semibold text-tinta">Ajuste de estoque</h2>
          <p className="mt-[3px] mb-5 text-[12.5px] text-texto-suave">
            Todo ajuste fica registrado com responsável e motivo.
          </p>

          <div className="mb-5 grid gap-3.5 sm:grid-cols-2">
            <div>
              <label htmlFor="sku" className="mb-1.5 block text-[12.5px] font-semibold text-texto">
                SKU
              </label>
              {/*
                `composto`: quem carrega a borda e o anel de foco é o
                INVÓLUCRO, porque o input de dentro não tem borda para
                colorir. Antes este bloco pintava a borda azul e o anel de
                forma FIXA — parecia focado sempre, e focar de verdade não
                mudava nada. O estado agora vem do `:focus-within`.
              */}
              <div className="composto">
                <Icone nome="barcode_scanner" tamanho={18} className="text-texto-fraco" />
                <input
                  id="sku"
                  value={sku}
                  onChange={(evento) => setSku(evento.target.value.replace(/\s/g, ''))}
                  // O leitor de código de barras é um teclado: ele digita os
                  // dígitos e dá Enter. Foco automático é o que faz "bipar"
                  // funcionar sem clicar no campo.
                  autoFocus
                  inputMode="numeric"
                  className="tabular"
                />
              </div>
              <p className="mt-1.5 text-[11.5px] text-texto-fraco">Bipe ou digite</p>
            </div>

            <div>
              <label
                htmlFor="quantidade"
                className="mb-1.5 block text-[12.5px] font-semibold text-texto"
              >
                Quantidade
              </label>
              <div className="composto">
                <input
                  id="quantidade"
                  value={quantidade}
                  onChange={(evento) => setQuantidade(evento.target.value.replace(/\D/g, ''))}
                  inputMode="numeric"
                  className="tabular font-semibold"
                />
                <span className="text-[13px] text-texto-suave">un</span>
              </div>
              <p className="mt-1.5 text-[11.5px] text-texto-fraco">
                Sempre positiva — o motivo define o sinal
              </p>
            </div>
          </div>

          <fieldset className="mb-5">
            <legend className="mb-2.5 text-[12.5px] font-semibold text-texto">Motivo</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {MOTIVOS.map((item) => {
                const ativo = item.chave === motivo
                return (
                  <button
                    key={item.chave}
                    type="button"
                    aria-pressed={ativo}
                    onClick={() => setMotivo(item.chave)}
                    className={`rounded-sistema border px-4 py-3.5 text-left ${
                      ativo
                        ? 'border-marca bg-marca-fundo shadow-[0_0_0_3px_rgba(37,99,235,0.12)]'
                        : 'border-borda bg-superficie'
                    }`}
                  >
                    <span className="mb-1.5 flex items-center gap-2.5">
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-sistema ${
                          ativo ? 'bg-marca-suave text-marca' : 'bg-divisoria text-texto'
                        }`}
                      >
                        <Icone nome={ICONE_MOTIVO[item.chave]} tamanho={17} />
                      </span>
                      <span className="text-[13.5px] font-semibold text-tinta">{item.rotulo}</span>
                    </span>
                    <span className="block text-[12.5px] leading-[1.5] text-texto-suave">
                      {item.dica}
                    </span>
                  </button>
                )
              })}
            </div>
          </fieldset>

          {escolhido?.sentido === 'ambos' && (
            <fieldset className="mb-5">
              <legend className="mb-2.5 text-[12.5px] font-semibold text-texto">
                A correção aumenta ou diminui o estoque?
              </legend>
              <Segmentado
                opcoes={[
                  { chave: 'entrada' as Sentido, rotulo: 'Aumenta' },
                  { chave: 'saida' as Sentido, rotulo: 'Diminui' },
                ]}
                valor={sentido}
                aoTrocar={setSentido}
                compacto
              />
            </fieldset>
          )}

          <div>
            <label
              htmlFor="observacao"
              className="mb-1.5 block text-[12.5px] font-semibold text-texto"
            >
              Observação
            </label>
            <textarea
              id="observacao"
              value={observacao}
              onChange={(evento) => setObservacao(evento.target.value.slice(0, 500))}
              placeholder="Peça de vitrine com risco na sola, sem condição de venda."
              className="h-[76px] w-full resize-none rounded-sistema border border-borda px-3 py-2.5 text-[13.5px] outline-none"
            />
          </div>

          {erro && (
            <div
              role="alert"
              className="mt-4 rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto"
            >
              {erro}
            </div>
          )}

          {resultado && (
            <div
              role="status"
              className="mt-4 rounded-sistema bg-ok-suave px-4 py-3 text-[13px] text-ok-texto"
            >
              <strong className="font-semibold">
                {resultado.produto} {resultado.cor} {resultado.tamanho}
              </strong>{' '}
              — saldo de {resultado.saldo_anterior} para {resultado.saldo_final} un.
            </div>
          )}
        </div>

        <div className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
          <h2 className="mb-3.5 font-titulo text-[15px] font-semibold text-tinta">
            Efeito do ajuste
          </h2>

          <Linha rotulo="SKU" valor={sku || '—'} />
          <Linha
            rotulo="Movimento"
            valor={
              quantidadeNum > 0 && sinal !== 0
                ? `${sinal < 0 ? '−' : '+'}${quantidadeNum} un`
                : '—'
            }
          />
          {podeVerCusto && (
            <Linha
              rotulo="Impacto a custo"
              valor={
                resultado ? formatarMoeda(resultado.impacto_custo_centavos) : 'calculado ao lançar'
              }
            />
          )}

          {/*
            O saldo final NÃO é calculado aqui. Quem sabe o saldo é o banco, e
            entre a tela abrir e o botão ser clicado uma venda pode ter saído.
            Mostrar "vai ficar 7" e gravar 6 seria pior que não mostrar nada.
          */}
          <p className="mt-3 border-t border-divisoria pt-3 text-[11.5px] leading-[1.5] text-texto-fraco">
            O saldo final vem do servidor depois do lançamento — entre abrir esta tela e clicar,
            uma venda pode ter saído.
          </p>

          <button
            type="button"
            disabled={!pronto || enviando}
            onClick={() => void enviar()}
            className={`mt-4 flex h-[46px] w-full items-center justify-center gap-2 rounded-sistema text-sm font-bold text-white ${
              pronto && !enviando ? 'bg-tinta' : 'bg-inativo'
            }`}
          >
            <Icone nome="check_circle" tamanho={19} />
            {enviando ? 'Lançando…' : 'Lançar ajuste'}
          </button>

          <div className="mt-4 flex gap-2.5 rounded-sistema border border-atencao-suave bg-atencao-fundo px-3.5 py-3">
            <Icone nome="warning" tamanho={19} className="text-atencao-texto" />
            <p className="text-[12.5px] leading-[1.55] text-atencao-texto">
              Perda e uso interno entram no relatório de quebra do mês. Ajuste não é para corrigir
              venda — para isso existe devolução.
            </p>
          </div>
        </div>
      </div>

      {pinPara && (
        <DialogoPIN
          permissao={pinPara}
          erro={erroPIN}
          ocupado={enviando}
          aoConfirmar={(pin) => void enviar(pin)}
          aoCancelar={() => {
            setPinPara('')
            setErroPIN('')
          }}
        />
      )}
    </>
  )
}

const ICONE_MOTIVO: Record<Motivo, NomeIcone> = {
  perda: 'report',
  sobra: 'add_box',
  uso_interno: 'checkroom',
  correcao: 'edit_note',
}

function Linha({ rotulo, valor }: { rotulo: string; valor: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-t border-divisoria py-2.5 text-[13px] first:border-t-0">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular text-right font-semibold text-tinta">{valor}</span>
    </div>
  )
}
